import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEffect, useState } from "react";
import { DataTable } from "@/components/custom/DataTable";
import TopBar from "@/components/custom/TopBar";
import SearchFilter from "@/components/custom/SearchFilter";
import i18n from "@/lang/i18n";
import { DateTime } from "luxon";
import PageLayout from "@/components/custom/PageLayout";
import { useCarDetailsTableColumns } from "../CarAllocated/CarsAllocatedTable/columns";
import { GMap } from "@/components/custom/GMap";
import { useTheme } from "@/themeConfigs/ThemeContext";
import DashboardService from "@/services/api/DashboardService";
import {
  CarDetailsTable,
  MarkerData,
} from "@/central-atoms/everest-fleet-pwa/types";
import { CarStatusDashboardIdleCarDetailsResponse } from "@/central-atoms/everest-fleet-pwa/responses";
import { BottomNav } from "@/components/custom/BottomNav";
import { useHubDetails } from "@/redux/hooks/useSupervisorDetails";
import { useLocation } from "react-router-dom";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";

export default function Idle() {
  const [tableData, setTableData] = useState<CarDetailsTable[]>([]);
  const [mapData, setMapData] = useState<MarkerData[]>([]);
  const [currentDateTime, setCurrentDateTime] = useState(
    DateTime.now().toFormat("dd LLLL, hh:mm a")
  );
  const hubDetails = useHubDetails();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const duration = queryParams.get("duration") || "30"; // default to 30 if not specified

  const idleCars = useSelector((state: RootState) => {
    switch (duration) {
      case "15":
        return state.carStatus.car_status?.idle_cars.fifteen_min;
      case "45":
        return state.carStatus.car_status?.idle_cars.forty_five_min;
      case "30":
      default:
        return state.carStatus.car_status?.idle_cars.thirty_min;
    }
  });

  const color = (() => {
    switch (duration) {
      case "15":
        return "supervisor-yellow";
      case "45":
        return "supervisor-red";
      case "30":
      default:
        return "supervisor-orange";
    }
  })();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(DateTime.now().toFormat("dd LLLL, hh:mm a"));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await DashboardService.getCarStatusDashboardDetails(
          hubDetails?.name,
          "idle",
          parseInt(duration)
        );

        const idleCarDetailsResponse =
          response as CarStatusDashboardIdleCarDetailsResponse;

        // Transforming the data for the table
        const transformedTableData =
          idleCarDetailsResponse.data.data.vehicle_details.map((vehicle) => ({
            et_id: vehicle.et_id,
            phone: vehicle.phone,
            vehicle_number: vehicle.car_number,
          }));

        // Transforming the data for the map
        const transformedMapData =
          idleCarDetailsResponse.data.data.vehicle_details.map((vehicle) => ({
            lat: vehicle.lat,
            lng: vehicle.lng,
            phone: vehicle.phone,
            et_id: vehicle.et_id,
            name: vehicle.name,
            vehicle_number: vehicle.car_number,
          }));

        setTableData(transformedTableData);
        setMapData(transformedMapData);
      } catch (error) {
        console.error("Failed to fetch car allocation details:", error);
      }
    };
    fetchData();
  }, [duration, hubDetails]);

  const columns = useCarDetailsTableColumns();
  const { theme } = useTheme();

  const mapCenter = mapData.length
    ? {
        lat:
          mapData.reduce((sum, marker) => sum + marker.lat, 0) / mapData.length,
        lng:
          mapData.reduce((sum, marker) => sum + marker.lng, 0) / mapData.length,
      }
    : { lat: 0, lng: 0 };

  return (
    <>
      <PageLayout>
        <TopBar
          color={theme.colors[color]}
          text={`Idle for more than ${duration} mins`} 
          number={idleCars ? idleCars.toString() : "0"}
        />
        <br />
        <Tabs defaultValue="list" className="w-full">
          <TabsList className="grid w-full grid-cols-2 h-12">
            <TabsTrigger variant="primary" value="map" className="py-2">
              Map View
            </TabsTrigger>
            <TabsTrigger variant="primary" value="list" className="py-2">
              List View
            </TabsTrigger>
          </TabsList>
          <TabsContent value="map">
            <GMap markers={mapData} center={mapCenter} />
          </TabsContent>
          <TabsContent value="list" className="py-3">
            <br />
            <DataTable
              columns={columns}
              data={tableData}
              headerColor="bg-primary text-primary-foreground"
              alignment="center"
            />
            <div className="mt-5 flex justify-between items-center">
              <p className="text-muted-foreground">As of {currentDateTime}</p>
            </div>
          </TabsContent>
        </Tabs>
      </PageLayout>
      <BottomNav active={1} />
    </>
  );
}
